import { deleteApi, getApi, postApi, putApi, putUploadImagesApi } from "../helpers/auth_helpers";

const base = process.env.REACT_APP_SERVERURL || "";

const ORDER_FILE_MAX_BYTES = 12 * 1024 * 1024;

const orderFileType = (file: File): string => {
  const mime = file?.type || "";
  const name = String(file?.name || "").toLowerCase();
  if (mime === "image/png" || name.endsWith(".png")) {
    return "image/png";
  }
  if (
    mime === "image/jpeg" ||
    mime === "image/jpg" ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg")
  ) {
    return "image/jpeg";
  }
  if (mime === "application/pdf" || name.endsWith(".pdf")) {
    return "application/pdf";
  }
  if (
    mime === "application/msword" ||
    mime === "application/doc" ||
    name.endsWith(".doc")
  ) {
    return "application/doc";
  }
  if (
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    mime === "application/docx" ||
    name.endsWith(".docx")
  ) {
    return "application/docx";
  }
  if (mime === "audio/webm" || name.endsWith(".webm")) {
    return "audio/webm";
  }
  if (mime === "audio/mpeg" || mime === "audio/mp3" || name.endsWith(".mp3")) {
    return "audio/mpeg";
  }
  if (mime === "audio/mp4" || mime === "audio/m4a" || name.endsWith(".m4a")) {
    return "audio/mp4";
  }
  if (mime === "audio/ogg" || name.endsWith(".ogg")) {
    return "audio/ogg";
  }
  return "";
};

export const listOrders = (params: {
  offset?: number;
  limit?: number;
  status?: string;
  acceptedStatus?: string;
  completeness?: string;
  q?: string;
}) => {
  const query = new URLSearchParams();
  query.set("offset", String(params.offset ?? 0));
  query.set("limit", String(params.limit ?? 10));
  if (params.status) query.set("status", params.status);
  else if (params.acceptedStatus) query.set("acceptedStatus", params.acceptedStatus);
  if (params.completeness) query.set("completeness", params.completeness);
  if (params.q) query.set("q", params.q);
  return getApi(`${base}/studio/orders?${query.toString()}`);
};

export const getOrder = (id: string) => getApi(`${base}/studio/orders/${id}`);

export const createOrder = (body: Record<string, unknown>) =>
  postApi(`${base}/studio/orders`, body);

export const updateOrder = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/studio/orders/${id}`, body);

export const updateOrderStatus = (
  id: string,
  body: {
    workflowStatus: string;
    stuckReason?: string | null;
    customStageLabel?: string | null;
  }
) => putApi(`${base}/studio/orders/${id}/status`, body);

export const deleteOrder = (id: string) =>
  deleteApi(`${base}/studio/orders/${id}`, {});

export const addOrderNote = (id: string, message: string) =>
  postApi(`${base}/studio/orders/${id}/notes`, { message });

export const duplicateOrder = (id: string) =>
  postApi(`${base}/studio/orders/${id}/duplicate`, {});

export const notifyOrderClient = (id: string) =>
  postApi(`${base}/studio/orders/${id}/notify-client`, {});

export const sendOrderConfirmation = (id: string) =>
  postApi(`${base}/studio/orders/${id}/send-confirmation`, {});

export const previewOrderConfirmation = (token: string) =>
  fetch(`${base}/public/orders/confirm/${encodeURIComponent(token)}`).then((response) => response.json());

export const confirmOrderDetails = (token: string) =>
  fetch(`${base}/public/orders/confirm/${encodeURIComponent(token)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.json());

export const searchOrderProducts = (q: string) => {
  const query = new URLSearchParams();
  if (q) query.set("q", q);
  return getApi(`${base}/studio/orders/product-matches?${query.toString()}`);
};

export const searchOrderCustomers = (q: string) => {
  const query = new URLSearchParams();
  if (q) query.set("q", q);
  return getApi(`${base}/studio/orders/customer-matches?${query.toString()}`);
};

export const uploadOrderDocument = async (
  file: File
): Promise<{ url: string; fileName: string; contentType: string }> => {
  const mime = orderFileType(file);
  if (!mime) {
    throw new Error("Upload a JPEG, PNG, PDF, Word, or audio file");
  }
  if (file.size > ORDER_FILE_MAX_BYTES) {
    throw new Error("File must be 12 MB or smaller");
  }
  const presigned: any = await postApi(`${base}/upload/studioOrderDocumentPresignedURL`, {
    fileType: mime,
  });
  const presignedUrl: string = presigned?.data;
  if (!presigned?.success || !presignedUrl) {
    throw new Error(presigned?.message || "Could not get upload URL");
  }
  const putRes: any = await putUploadImagesApi(presignedUrl, file, {
    "Content-Type": mime,
  });
  if (putRes && putRes.ok === false) {
    throw new Error("Could not upload the file");
  }
  return {
    url: String(presignedUrl).split("?")[0],
    fileName: file.name,
    contentType: mime,
  };
};
